/**
 * LOGOS v5.1 — Emergence Gate Dashboard
 * Visualizes the 5 necessary conditions (gates) for emergence events.
 * Each gate is a necessary condition; ALL must be open for emergence.
 *
 * C₁: Λ ≥ 0.60  — Alignment sufficient
 * C₂: C ≥ 0.50  — Coherence minimum
 * C₃: E ≥ 0.70  — Domains synchronizing (Emergence Index)
 * C₄: S ≤ 0.30  — Low entropy (little noise)
 * C₅: Λ̇ ≥ 0    — Lambda not falling
 *
 * ERS = min(C₁,C₂,C₃,C₄,C₅) × [0.30Λ + 0.25C + 0.20E + 0.15(1−S) + 0.10Λ̇]
 * If ANY gate fails, ERS = 0.
 *
 * Reference: LOGOS HUMANO v5.0 Manual — PARTE IV, Las 5 Puertas de Criticalidad
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';

const GATE_DEFS = [
  { id: 'lambda',      label: 'Λ ≥ 0.60',  fullLabel: 'C₁ Alignment',       threshold: 0.60, compare: 'gte', color: '#A855F7',
    tip: 'Lambda (Λ) must be ≥ 0.60 for the ordered phase. Measures your overall alignment with the Logos signal. Raise it via faith, meditation, presence, service.' },
  { id: 'coherence',   label: 'C ≥ 0.50',  fullLabel: 'C₂ Coherence',       threshold: 0.50, compare: 'gte', color: '#06B6D4',
    tip: 'Coherence (C) must be ≥ 0.50. Measures inter-domain balance — all 7 domains working together. A single collapsed domain destroys coherence.' },
  { id: 'emergence',   label: 'E ≥ 0.70',  fullLabel: 'C₃ Synchrony',       threshold: 0.70, compare: 'gte', color: '#10B981',
    tip: 'Emergence Index (E) must be ≥ 0.70. Measures whether your domains are synchronizing over time — moving together, not diverging.' },
  { id: 'entropy',     label: 'S ≤ 0.30',  fullLabel: 'C₄ Low Noise',       threshold: 0.30, compare: 'lte', color: '#F59E0B',
    tip: 'Entropy (S) must be ≤ 0.30. Measures internal noise/chaos. Lower is better. Reduce it by simplifying, resting, and practicing stillness.' },
  { id: 'lambdaDot',   label: 'Λ̇ ≥ 0',    fullLabel: 'C₅ Λ Rising',        threshold: 0.0,  compare: 'gte', color: '#EC4899',
    tip: 'Lambda derivative (Λ̇) must be ≥ 0. Lambda must not be falling. If negative, your alignment is declining — reconnect immediately.' },
];

function safeRoundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

export default function EmergenceGateDashboard({
  lambda = 0.5,
  coherence = 0.5,
  emergenceIndex = 0.5,
  entropy = 0.5,
  lambdaDot = 0,
  ers = 0,
  width: propWidth = 380,
  height: propHeight = 340,
  t = (k) => k,
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const hitRegions = useRef([]);
  const [tooltip, setTooltip] = useState(null);
  const [containerW, setContainerW] = useState(propWidth);
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) { const w = e.contentRect.width; if (w > 0) setContainerW(w); }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);
  const width = Math.min(containerW, propWidth);
  const height = Math.round(width * (propHeight / propWidth));

  const values = {
    lambda,
    coherence,
    emergence: emergenceIndex,
    entropy,
    lambdaDot,
  };

  const gates = GATE_DEFS.map(g => {
    const val = values[g.id];
    const open = (g.compare === 'gte') ? val >= g.threshold : val <= g.threshold;
    const distance = (g.compare === 'gte')
      ? (val - g.threshold) / (Math.max(0.01, 1 - g.threshold))
      : (g.threshold - val) / Math.max(0.01, g.threshold);
    return { ...g, value: val, open, distance: Math.max(-1, Math.min(1, distance)) };
  });

  const openCount = gates.filter(g => g.open).length;
  const allOpen = openCount === 5;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 2;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    // Background
    ctx.fillStyle = '#0D0B1A';
    ctx.fillRect(0, 0, width, height);

    const cx = width / 2;
    const cy = height / 2 - 10;
    const outerR = Math.min(width, height) * 0.38;
    const innerR = outerR * 0.55;

    // Draw 5 gate arcs around a central ring
    const arcGap = 0.06; // gap between arcs in radians
    const totalArc = (2 * Math.PI - 5 * arcGap) / 5;
    const startOffset = -Math.PI / 2; // start from top

    gates.forEach((gate, i) => {
      const startAngle = startOffset + i * (totalArc + arcGap);
      const endAngle = startAngle + totalArc;

      // Outer arc (gate status)
      ctx.lineWidth = 14;
      ctx.lineCap = 'round';

      if (gate.open) {
        // Green glow for open gate
        const grad = ctx.createLinearGradient(
          cx + Math.cos(startAngle) * outerR, cy + Math.sin(startAngle) * outerR,
          cx + Math.cos(endAngle) * outerR, cy + Math.sin(endAngle) * outerR
        );
        grad.addColorStop(0, gate.color);
        grad.addColorStop(1, gate.color + 'CC');
        ctx.strokeStyle = grad;

        // Glow effect
        ctx.shadowColor = gate.color;
        ctx.shadowBlur = 12;
      } else {
        // Dim red for closed gate
        ctx.strokeStyle = 'rgba(239,68,68,0.25)';
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
      }

      ctx.beginPath();
      ctx.arc(cx, cy, outerR, startAngle, endAngle);
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Inner progress arc (how close to threshold)
      const progress = Math.max(0, Math.min(1, gate.open ? 1 : (gate.distance + 1) / 2));
      const progressEnd = startAngle + totalArc * progress;
      ctx.lineWidth = 4;
      ctx.strokeStyle = gate.open ? gate.color + '60' : 'rgba(255,255,255,0.08)';
      ctx.beginPath();
      ctx.arc(cx, cy, innerR, startAngle, progressEnd);
      ctx.stroke();

      // Gate label
      const labelAngle = (startAngle + endAngle) / 2;
      const labelR = outerR + 20;
      const lx = cx + Math.cos(labelAngle) * labelR;
      const ly = cy + Math.sin(labelAngle) * labelR;

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Gate icon
      ctx.font = 'bold 10px Arial';
      ctx.fillStyle = gate.open ? gate.color : '#EF4444AA';
      ctx.fillText(gate.open ? '●' : '○', lx, ly - 8);

      // Gate label
      ctx.font = '9px Arial';
      ctx.fillStyle = gate.open ? '#E8E4F0' : '#8B85A0';
      ctx.fillText(gate.label, lx, ly + 4);

      // Gate name
      ctx.font = '7px Arial';
      ctx.fillStyle = '#6B6580';
      ctx.fillText(gate.fullLabel, lx, ly + 15);

      // Current value
      ctx.font = 'bold 9px Arial';
      ctx.fillStyle = gate.open ? gate.color : '#EF4444';
      const valStr = gate.id === 'entropy'
        ? gate.value.toFixed(2)
        : gate.value.toFixed(2);
      ctx.fillText(valStr, lx, ly + 26);
    });

    // Center: emergence status
    if (allOpen) {
      // Glowing emergence symbol
      const t = Date.now() * 0.002;
      const pulse = 0.7 + 0.3 * Math.sin(t);

      const emergeGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, innerR * 0.8);
      emergeGrad.addColorStop(0, `rgba(168,85,247,${0.25 * pulse})`);
      emergeGrad.addColorStop(0.5, `rgba(168,85,247,${0.08 * pulse})`);
      emergeGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = emergeGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, innerR * 0.8, 0, Math.PI * 2);
      ctx.fill();

      ctx.font = 'bold 28px Arial';
      ctx.fillStyle = '#A855F7';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('✦', cx, cy - 12);

      ctx.font = 'bold 10px Arial';
      ctx.fillStyle = '#10B981';
      ctx.fillText(t('emergence.gates.allOpen'), cx, cy + 10);

      ctx.font = '9px Arial';
      ctx.fillStyle = '#8B85A0';
      ctx.fillText('5/5', cx, cy + 24);
    } else {
      // Show count and what's missing
      ctx.font = 'bold 22px Arial';
      ctx.fillStyle = openCount >= 3 ? '#F59E0B' : '#EF4444';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${openCount}/5`, cx, cy - 8);

      ctx.font = '9px Arial';
      ctx.fillStyle = '#8B85A0';
      const closedGates = gates.filter(g => !g.open);
      const missing = closedGates.map(g => g.id === 'entropy' ? 'S↓' : g.id === 'lambda' ? 'Λ↑' : g.id === 'coherence' ? 'C↑' : g.id === 'emergence' ? 'E↑' : 'Λ̇↑').join(' ');
      ctx.fillText(t('emergence.gates.missing'), cx, cy + 8);
      ctx.font = 'bold 9px Arial';
      ctx.fillStyle = '#EF4444';
      ctx.fillText(missing, cx, cy + 21);
    }

    // Bottom: ERS bar
    const barY = height - 35;
    const barW = width - 60;
    const barH = 8;
    const barX = 30;

    // Background
    safeRoundRect(ctx, barX, barY, barW, barH, 4);
    ctx.fillStyle = 'rgba(255,255,255,0.05)';
    ctx.fill();

    // Fill
    const ersW = Math.max(0, Math.min(1, ers)) * barW;
    safeRoundRect(ctx, barX, barY, ersW, barH, 4);
    const ersGrad = ctx.createLinearGradient(barX, 0, barX + barW, 0);
    ersGrad.addColorStop(0, '#EF4444');
    ersGrad.addColorStop(0.35, '#F59E0B');
    ersGrad.addColorStop(0.65, '#10B981');
    ersGrad.addColorStop(1, '#A855F7');
    ctx.fillStyle = ersGrad;
    ctx.fill();

    // ERS label
    ctx.font = '8px Arial';
    ctx.fillStyle = '#8B85A0';
    ctx.textAlign = 'left';
    ctx.fillText('ERS', barX, barY - 4);
    ctx.textAlign = 'right';
    ctx.fillStyle = ers > 0.5 ? '#10B981' : '#F59E0B';
    ctx.font = 'bold 9px Arial';
    ctx.fillText((ers * 100).toFixed(0) + '%', barX + barW, barY - 4);

    // Threshold marker
    const threshX = barX + 0.5 * barW;
    ctx.strokeStyle = '#FFFFFF40';
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 2]);
    ctx.beginPath();
    ctx.moveTo(threshX, barY - 1);
    ctx.lineTo(threshX, barY + barH + 1);
    ctx.stroke();
    ctx.setLineDash([]);

    // Title
    ctx.font = 'bold 10px Arial';
    ctx.fillStyle = '#E8E4F0';
    ctx.textAlign = 'center';
    ctx.fillText(t('emergence.gates.title'), cx, 14);

    ctx.font = '8px Arial';
    ctx.fillStyle = '#6B6580';
    ctx.fillText(t('emergence.gates.subtitle'), cx, 26);

    // Build hit regions for tooltips
    const regions = [];
    gates.forEach((gate, i) => {
      const startAngle = startOffset + i * (totalArc + arcGap);
      const endAngle = startAngle + totalArc;
      const labelAngle = (startAngle + endAngle) / 2;
      const labelR2 = outerR + 20;
      const lx = cx + Math.cos(labelAngle) * labelR2;
      const ly = cy + Math.sin(labelAngle) * labelR2;
      regions.push({ x: lx, y: ly, r: 28, gate });
    });
    // ERS bar region
    regions.push({ x: barX + barW / 2, y: barY, r: 20, gate: {
      fullLabel: 'ERS',
      label: `${(ers * 100).toFixed(0)}%`,
      color: ers > 0.5 ? '#10B981' : '#F59E0B',
      open: ers > 0.5,
      value: ers,
      threshold: 0.50,
      tip: `Emergence Readiness Score = ${(ers * 100).toFixed(1)}%. ERS = min(C₁..C₅) × weighted sum. If ANY gate is closed, ERS = 0. Threshold: 50%.`,
    }});
    // Center region
    regions.push({ x: cx, y: cy, r: 35, gate: {
      fullLabel: allOpen ? 'EMERGENCE READY' : `${openCount}/5 Gates`,
      label: allOpen ? '5/5' : `${openCount}/5`,
      color: allOpen ? '#10B981' : openCount >= 3 ? '#F59E0B' : '#EF4444',
      open: allOpen,
      value: openCount,
      threshold: 5,
      tip: allOpen
        ? 'All 5 criticality gates are OPEN. The system is in emergence-ready state. Emergence events (insights, synchronicities, flow) are most probable now.'
        : `${openCount} of 5 gates open. ${5 - openCount} gate(s) still closed. ALL 5 must be open simultaneously for emergence. Like a rocket launch: one failed check = no launch.`,
    }});
    hitRegions.current = regions;

  }, [lambda, coherence, emergenceIndex, entropy, lambdaDot, ers, width, height, t]);

  const handleMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    for (const region of hitRegions.current) {
      const dx = mx - region.x;
      const dy = my - region.y;
      if (dx * dx + dy * dy < region.r * region.r) {
        setTooltip({ x: mx, y: my, gate: region.gate });
        return;
      }
    }
    setTooltip(null);
  }, []);

  const handleMouseLeave = useCallback(() => setTooltip(null), []);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <canvas
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          width, height,
          maxWidth: '100%',
          borderRadius: 12,
          cursor: tooltip ? 'help' : 'default',
        }}
      />
      {tooltip && (
        <div style={{
          position: 'absolute',
          left: Math.min(tooltip.x + 12, width - 220),
          top: Math.max(tooltip.y - 70, 4),
          maxWidth: 210,
          padding: '10px 12px',
          background: 'rgba(10,10,30,0.95)',
          border: `1px solid ${tooltip.gate.color}50`,
          borderRadius: 8,
          color: '#E0E0F0',
          fontSize: 10,
          lineHeight: 1.6,
          fontFamily: "'Inter', -apple-system, sans-serif",
          pointerEvents: 'none',
          zIndex: 10,
          boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
        }}>
          <div style={{ fontWeight: 'bold', color: tooltip.gate.color, fontSize: 11, marginBottom: 3 }}>
            {tooltip.gate.open ? '●' : '○'} {tooltip.gate.fullLabel}
          </div>
          <div style={{ marginBottom: 4 }}>
            <span style={{ color: '#aaa8c0' }}>Value: </span>
            <span style={{ fontWeight: 'bold', color: tooltip.gate.open ? '#10B981' : '#EF4444' }}>
              {typeof tooltip.gate.value === 'number' ? tooltip.gate.value.toFixed(3) : tooltip.gate.value}
            </span>
            <span style={{ color: '#666', marginLeft: 4 }}>({tooltip.gate.label})</span>
          </div>
          <div style={{ fontSize: 9, color: '#9994b0', lineHeight: 1.5 }}>
            {tooltip.gate.tip}
          </div>
        </div>
      )}
    </div>
  );
}
